export type UpcomingPatientAppointment = {
	appointmentId: number
	patientId: number
	locationId: number
	address: string
	startTimeUtc: string
	startTimeLocal: string
	duration: number
	types: string[]
	isConfirmed: boolean
	isConfirmable: boolean
	startTimeFormatted?: string
}

export type AppointmentResultReportCondition = {
	title: string
	description: string
}

export type AppointmentResultReportNextSteps = {
	description: string
	title: string
}

export type AppointmentResultReportResponse = {
	isLocked: boolean
	results?: AppointmentResultReport
	appointmentId?: number
}

export type AppointmentResultReport = {
	canBookNextAppointment: boolean
	summary: string
	healthScore: number
	conditions: AppointmentResultReportCondition[]
	nextSteps: AppointmentResultReportNextSteps
	nextAppointmentType: string
}

export type AppointmentTypeRef = {
	value: string
	name: string
}

/** One of the patient's upcoming visits, from `GET /appointments/upcoming`. */
export type UpcomingVisit = {
	appointmentId: number
	types: AppointmentTypeRef[]
	startTimeUtc: string
	startTimeLocal: string
	/**
	 * When the visit is over: its own end where the studio wrote one, its duration where not.
	 *
	 * The server keeps a visit in this list until this instant, so it is also the moment the card
	 * stops leading Home.
	 */
	endTimeUtc: string
	/** Set client-side, e.g. "Sep 12th at 9:30 AM". */
	startTimeFormatted?: string
	/**
	 * How long the visit runs, in minutes, as the appointment was booked.
	 *
	 * The server's, not a subtraction of `endTimeUtc` less `startTimeUtc` — that arithmetic would
	 * give the same answer today and quietly stop being the booked length the day a studio writes
	 * an end that is not one. provider-kapi#1166 sends it off `AppointmentDetails`, where it has
	 * lived since long before this endpoint existed. Optional while that deploy travels, and the
	 * type line is the type alone until it lands.
	 */
	durationInMinutes?: number
	locationId: number
	/**
	 * The studio, written the way a member is standing in front of it.
	 *
	 * All four arrived together in provider-kapi#1162, read off the `Location` the mapper was
	 * already holding. They are optional here only while that deploy reaches every environment —
	 * staging was still answering without them when this was written, and a screen that assumes
	 * them renders "Cannot read property 'street1' of undefined". Make them required, and drop
	 * the `address`-only branch in `VisitCardDetail`, once they are everywhere.
	 */
	/** Which studio, in the word a member uses for it: "Tribeca", not a street number. */
	locationName?: string
	/** The building or suite, where the street alone does not find the door. */
	siteName?: string | null
	/** The parts run together onto one line, for where an address is a caption. */
	address: string
	/** The same address unjoined, for where it is a destination. */
	structuredAddress?: {
		street1: string
		street2: string | null
		city: string
		state: string
		zip: string
	}
	/** Where the door is. The server's `Location` holds it non-null. */
	coordinates?: { lat: number; lng: number }
	isConfirmable: boolean
	isConfirmed: boolean
	/**
	 * Whether anybody is actually waiting on a confirmation.
	 *
	 * Wally releases a visit booked four or more days out when a member never confirms, and
	 * provider-kapi#1167 puts that behind a switch: with it flipped, nothing is released and
	 * confirming becomes a courtesy. A Confirm button whose deadline has no consequence teaches
	 * members to ignore us, so where this is false the card never asks — it counts down to the
	 * door instead, from the moment the visit is booked.
	 *
	 * Optional while that deploy travels, and read as `true`: asking is how Wally has always
	 * worked.
	 */
	confirmationRequired?: boolean
	/**
	 * When the studio starts asking for a confirmation: the visit's start, less the server's
	 * confirmation window.
	 *
	 * `isConfirmable` says whether the window is open now; this says when it opens, which is the
	 * only way to tell a member that nobody is waiting on them *yet* rather than that they have
	 * missed something. Computed beside `isConfirmable` in provider-kapi so the two cannot
	 * disagree.
	 */
	confirmationOpensAt: string
	/**
	 * The studio's own way in: a photograph, a title and a line, per step.
	 *
	 * Authored per studio in the ops console, because "through the lobby and up to six" is true
	 * of one building and nonsense about another. Empty where a studio has none.
	 */
	arrivalSteps?: ArrivalStep[]
	/** Arriving: whether the door is open, and when it opens and closes. */
	checkIn?: CheckInBlock
	/**
	 * The building's own credential, which is nothing to do with our arrival flow.
	 *
	 * WeWork emails the pass to `wework-checkin+{appointmentId}@`; messaging-kapi decodes the QR
	 * off the attachment, keeps the welkio link out of the body, and publishes `SitePassCreated`.
	 * So a pass exists from whenever that email lands — not from when a member tells us they have
	 * arrived, which is what the two used to be tangled together by.
	 *
	 * Null at a studio with its own front door, and null while the email has not come.
	 */
	sitePass?: SitePass | null
	/** The scheduling link the visit was booked through; a pending form carries the same id. */
	scheduleLinkId: string | null
}

/** One beat of the walk from the street to the chair. */
export type ArrivalStep = {
	title: string
	description: string
	imageUrl: string
}

/**
 * The four states of a door, as the server decides them.
 *
 * The same enum the web check-in page reads, from the same thirty-minute window either side of
 * the start — so the page a member reaches by text and this screen cannot disagree.
 */
export type CheckInStatus = 'NOT_YET_OPEN' | 'ENABLED' | 'EXPIRED' | 'CHECKED_IN'

export type CheckInBlock = {
	status: CheckInStatus
	/** Both edges, so nothing here has to know the window is thirty minutes. */
	opensAt: string
	closesAt: string
	/**
	 * Where the building's pass used to ride, revealed only once a member was checked in.
	 *
	 * It has its own field now. This one is read as a fallback while the deploy that moved it
	 * reaches every environment, and goes with that branch in `sitePassOf`.
	 */
	code?: string | null
}

/**
 * A pass into the building the studio is inside: a code to draw, and whose desk to draw it at.
 *
 * WeWork's email sometimes carries only a one-time link instead of a scannable QR. That is not a
 * pass as far as this screen is concerned — a member standing at a reception desk needs something
 * to hold up — so the server sends no `sitePass` at all in that case and the row is absent.
 */
export type SitePass = {
	/** The building, in its own name: "WeWork". */
	name: string
	/** The QR's payload, decoded off the attachment by messaging-kapi. */
	code: string
}

/** One bookable opening at a studio, studio-local. */
export type AvailableSlot = {
	/** "HH:mm:ss", the studio's wall clock. */
	startTime: string
	durationInMinutes: number
	podId: number
}

export type StudioAvailability = {
	locationId: number
	name: string
	timezone: string
	isHomeStudio: boolean
	hasAvailability: boolean
	address: string
	structuredAddress: {
		street1: string
		street2: string | null
		city: string
		state: string
		zip: string
	}
	coordinates: { lat: number; lng: number } | null
	siteName: string | null
	/** Keyed "yyyy-MM-dd"; every day of the week is present, empty ones included. */
	slotsByDate: Record<string, AvailableSlot[] | undefined>
}

export type BookingContext = {
	appointmentType: { value: string; name: string; durationInMinutes: number }
	homeLocationId: number
	geoId: number
	/** yyyy-MM-dd */
	earliestBookableDate: string
	maxBookableDate: string
}

/** A city or region the patient can be seen in; several studios sit inside one. */
export type Geo = { id: number; name: string }

export type WeeklyAvailability = {
	geoId: number
	/** Everywhere the patient could book, home first. One entry means nothing to choose. */
	geos: Geo[]
	weekStart: string
	weekEnd: string
	minWeekStart: string
	maxWeekStart: string
	bookingContext: BookingContext
	locations: StudioAvailability[]
}

export type BookVisitRequest = {
	locationId: number
	podId: number
	/**
	 * The studio's wall clock, "2026-04-20T09:00:00", and never an instant. A trailing offset
	 * is accepted and then silently dropped, which would book a different time than the
	 * patient chose.
	 */
	startTimeLocal: string
	idempotencyKey: string
	rescheduleAppointmentId?: number
}

export type BookedVisit = {
	appointmentId: number
	appointmentType: { value: string; name: string; durationInMinutes: number }
	startTimeUtc: string
	startTimeLocal: string
	durationInMinutes: number
	locationId: number
	locationName: string
	address: string
	timezone: string
	isConfirmed: boolean
	/** The visit this one replaced; null when the release did not go through. */
	releasedAppointmentId: number | null
}

/** The chosen time went while the patient was looking at it; a 409 carrying this code. */
export const SLOT_UNAVAILABLE = 'SLOT_UNAVAILABLE'

/** The same key is already booking. Also a 409, which is why the code decides, not the status. */
export const BOOKING_IN_PROGRESS = 'BOOKING_IN_PROGRESS'
